import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useActivityApi custom hook for activity API operations.
 * - Provides CRUD operations for activities using Apollo Client and GraphQL.
 * - Handles cache updates for activity queries and mutations.
 * - Exposes helpers for reading/writing/updating activities in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting activities.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const ACTIVITY_QUERIES = {
  // Query to fetch all activities
  GET_ACTIVITIES: gql`
    query GetActivities {
      GetActivities {
        activity_id
        activity_name
        activity_code
        tag_type
        tags {
          tag_id
          tag_name
          tag_type
        }
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch a activity by ID
  GET_ACTIVITY: gql`
    query GetActivityById($activity_id: ID!) {
      GetActivityById(activity_id: $activity_id) {
        activity_id
        activity_name
        activity_code
        tag_type
        tags {
          tag_id
          tag_name
          tag_type
        }
        is_active
        created_on
        updated_on
      }
    }
  `,
  GET_ACTIVITIES_BY_TAG_TYPE: gql`
    query GetActivitiesByTagType($tag_type: String!) {
      GetActivitiesByTagType(tag_type: $tag_type) {
        activity_id
        activity_name
        activity_code
        tags {
          tag_id
          tag_name
          tag_type
        }
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to add a new activity
  ADD_ACTIVITY: gql`
    mutation AddActivity(
      $activity_name: String!
      $tag_id: [ID!]!
      $tag_type: String!
    ) {
      AddActivity(
        activity_name: $activity_name
        tag_id: $tag_id
        tag_type: $tag_type
      ) {
        activity_id
        activity_name
        activity_code
        tag_type
        tags {
          tag_id
          tag_name
          tag_type
        }
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing activity
  UPDATE_ACTIVITY: gql`
    mutation UpdateActivity(
      $activity_id: ID!
      $activity_name: String
      $tag_id: [ID!]
      $tag_type: String
    ) {
      UpdateActivity(
        activity_id: $activity_id
        activity_name: $activity_name
        tag_id: $tag_id
        tag_type: $tag_type
      ) {
        activity_id
        activity_name
        activity_code
        tag_type
        tags {
          tag_id
          tag_name
          tag_type
        }
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a activity
  DELETE_ACTIVITY: gql`
    mutation DeleteActivity($activity_id: ID!) {
      DeleteActivity(activity_id: $activity_id) {
        activity_id
        activity_name
        activity_code
        tag_type
        tags {
          tag_id
          tag_name
          tag_type
        }
        is_active
        created_on
        updated_on
      }
    }
  `,
};

/**
 * Cache operations for activities.
 * - readActivities: Reads activities from Apollo cache.
 * - writeActivities: Writes activities to Apollo cache.
 * - updateActivityInCache: Updates a activity in Apollo cache.
 */
const cacheOperations = {
  readActivities: () => {
    try {
      const { GetActivities = [] } =
        client.readQuery({
          query: ACTIVITY_QUERIES.GET_ACTIVITIES,
        }) || {};
      return GetActivities;
    } catch {
      return [];
    }
  },

  writeActivities: (activities) => {
    if (!activities) return;
    client.writeQuery({
      query: ACTIVITY_QUERIES.GET_ACTIVITIES,
      data: { GetActivities: activities },
    });
  },

  updateActivityInCache: (updatedActivity) => {
    const existingActivities = cacheOperations.readActivities();
    const updatedActivities = existingActivities.map((activity) =>
      activity.activity_id === updatedActivity.activity_id
        ? updatedActivity
        : activity
    );
    cacheOperations.writeActivities(updatedActivities);
  },
};

export const useActivityApi = () => {
  // Mutation for adding a activity
  const [addActivityMutation] = useMutation(ACTIVITY_QUERIES.ADD_ACTIVITY, {
    update(cache, { data: { AddActivity } }) {
      try {
        const existingData = cache.readQuery({
          query: ACTIVITY_QUERIES.GET_ACTIVITIES,
        });
        if (existingData) {
          cache.writeQuery({
            query: ACTIVITY_QUERIES.GET_ACTIVITIES,
            data: {
              GetActivities: [AddActivity, ...existingData.GetActivities],
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after adding activity:", error);
      }
    },
  });

  // Mutation for updating a activity
  const [updateActivityMutation] = useMutation(ACTIVITY_QUERIES.UPDATE_ACTIVITY, {
    update(cache, { data: { UpdateActivity } }) {
      try {
        const existingData = cache.readQuery({
          query: ACTIVITY_QUERIES.GET_ACTIVITIES,
        });
        if (existingData) {
          const newData = existingData.GetActivities.map((activity) =>
            activity.activity_id === UpdateActivity.activity_id ? UpdateActivity : activity
          );
          cache.writeQuery({
            query: ACTIVITY_QUERIES.GET_ACTIVITIES,
            data: { GetActivities: newData },
          });
        }
      } catch (error) {
        console.error("Error updating cache after updating activity:", error);
      }
    },
  });

  // Mutation for deleting a activity
  const [deleteActivityMutation] = useMutation(ACTIVITY_QUERIES.DELETE_ACTIVITY, {
    update(cache, { data: { DeleteActivity } }) {
      try {
        const existingData = cache.readQuery({
          query: ACTIVITY_QUERIES.GET_ACTIVITIES,
        });
        if (existingData) {
          cache.writeQuery({
            query: ACTIVITY_QUERIES.GET_ACTIVITIES,
            data: {
              GetActivities: existingData.GetActivities.filter(
                (activity) => activity.activity_id !== DeleteActivity.activity_id
              ),
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after deleting activity:", error);
      }
    },
  });

  /**
   * Fetches all activities from the backend and updates the cache.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getActivities = async () => {
    try {
      const { data } = await client.query({
        query: ACTIVITY_QUERIES.GET_ACTIVITIES,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      return { success: true, data: data.GetActivities };
    } catch (error) {
      console.error("Error fetching activities:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a activity by its ID.
   * @param {string|number} activity_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getActivityById = async (activity_id) => {
    try {
      const { data } = await client.query({
        query: ACTIVITY_QUERIES.GET_ACTIVITY,
        variables: { activity_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetActivityById };
    } catch (error) {
      console.error("Error fetching activity:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches activities by tag_type from the backend.
   * @param {string} tag_type
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getActivitiesByTagType = async (tag_type) => {
    try {
      const { data } = await client.query({
        query: ACTIVITY_QUERIES.GET_ACTIVITIES_BY_TAG_TYPE,
        variables: { tag_type },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      return { success: true, data: data.GetActivitiesByTagType };
    } catch (error) {
      console.error("Error fetching activities by tag_type:", error);
      return { success: false, error: error.message };
    }
  }
    /**
     * Creates a new activity.
     * @param {Object} formData - { activity_name, activity_code, is_active }
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const createActivity = async (formData) => {
      try {
        const { data } = await addActivityMutation({
          variables: {
            activity_name: formData.activity_name,
            tag_id: formData.tag_id,
            tag_type: formData.tag_type
          },
        });
        return { success: true, data: data.AddActivity };
      } catch (error) {
        console.error("Error creating activity:", error);
        return { success: false, error: error.message };
      }
    };

    /**
     * Updates an existing activity.
     * @param {string|number} activityId
     * @param {Object} formData - { activity_name, activity_code, is_active }
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const updateActivity = async (activityId, formData) => {
      try {
        const { data } = await updateActivityMutation({
          variables: {
            activity_id: activityId,
            activity_name: formData.activity_name,
            tag_id: formData.tag_id,
            tag_type: formData.tag_type
          },
        });
        return { success: true, data: data.UpdateActivity };
      } catch (error) {
        console.error("Error updating activity:", error);
        return { success: false, error: error.message };
      }
    };

    /**
     * Deletes a activity by its ID.
     * @param {string|number} activity_id
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const deleteActivity = async (activity_id) => {
      try {
        const { data } = await deleteActivityMutation({
          variables: { activity_id },
        });
        return { success: true, data: data.DeleteActivity };
      } catch (error) {
        console.error("Error deleting activity:", error);
        return { success: false, error: error.message };
      }
    };

    return {
      getActivities,
      getActivitiesByTagType,
      getActivityById,
      createActivity,
      updateActivity,
      deleteActivity,
      cacheOperations,
    };
  };
